// src/useQueryParams.js
import { useMemo, useCallback } from "react";
import { useLocation, useHistory } from "react-router-dom";

export default function useQueryParams(schema) {
  const { search } = useLocation();
  const history = useHistory();

  // 1) read raw, turn into { [k]: string }
  const raw = useMemo(
    () => Object.fromEntries(new URLSearchParams(search).entries()),
    [search]
  );

  // 2) safe-parse raw → { success, data, error }
  const params = useMemo(() => {
    const result = schema.safeParse(raw);

    if (result.success) {
      // valid + defaults applied for missing keys
      return result.data;
    }

    // parsing failed: log if you want, then fallback to pure defaults
    console.warn(
      "Invalid query params, falling back to defaults:",
      result.error
    );
    // `schema.parse({})` will fill in *all* per-field defaults
    return schema.parse({});
  }, [schema, raw]);

  // 3) setter merges & pushes
  const setParams = useCallback(
    (updater) => {
      const next = typeof updater === "function" ? updater(params) : updater;
      const sp = new URLSearchParams();
      for (let [k, v] of Object.entries(next)) {
        if (v == null) continue;
        sp.set(k, Array.isArray(v) ? v.join(",") : String(v));
      }
      history.replace({ search: sp.toString() });
    },
    [history, params]
  );

  return { params, setParams };
}
